import I18n from '../../../config/i18n';
import {
  AdviceValues,
  FixedPrice,
  GuestPriceInfo,
  UsageBasedPrice,
} from '../../../features/prices/types';
import './PriceDetails.css';

interface PriceDetailsProps {
  priceInfo: GuestPriceInfo;
}

const unitsPerConsumption = ['per_cubic_meter', 'per_kwh', 'per_liter', 'per_hour'];

function showUnit(cost: FixedPrice | UsageBasedPriceWithOptionalAdvice): boolean {
  return !('advice' in cost && (cost.advice === 'bring' || cost.advice === 'included'));
}

function UnitInclConsumption({ unit }: { unit: string }): JSX.Element | null {
  if (unitsPerConsumption.includes(unit)) {
    return (
      <>
        {[
          I18n.t(`activerecord.attributes.cost.${unit}`),
          I18n.t(`activerecord.attributes.cost.per_consumption`),
        ].join(', ')}
      </>
    );
  }
  return <>{I18n.t(`activerecord.attributes.cost.${unit}`)}</>;
}

function FixedCost({ cost }: { cost: FixedPrice }): JSX.Element {
  const showAsIncluded = cost.totalPrice === 0;

  return (
    <>
      <div className="pricedetails__label">
        <div>{cost.title}</div>
        {!showAsIncluded && (
          <div className="pricedetails__unit">
            ({I18n.l('currency', cost.price)}
            {showUnit(cost) && (
              <>
                {' '}
                <UnitInclConsumption unit={cost.unit} />
              </>
            )}
            )
          </div>
        )}
      </div>
      <div className="pricedetails__price">
        {showAsIncluded
          ? I18n.t('travel_booking_price.included')
          : I18n.l('currency', cost.totalPrice)}
      </div>
    </>
  );
}

function UsageBasedPriceDisplay({
  price,
  showAsIncluded,
  advice,
}: {
  price: number;
  showAsIncluded: boolean;
  advice: AdviceValues[number] | null | undefined;
}): JSX.Element {
  if (showAsIncluded) {
    return I18n.t('travel_booking_price.included');
  }
  if (advice != null) {
    return (
      <>
        {I18n.t(`travel_booking_price.extras.type_${advice}`)}
        {advice === 'on_request' && price > 0 && (
          <>
            ,<br /> {I18n.l('currency', price)}
          </>
        )}
      </>
    );
  }
  return I18n.l('currency', price);
}

interface UsageBasedPriceWithOptionalAdvice extends Omit<UsageBasedPrice, 'advice'> {
  advice?: AdviceValues[number] | null;
}

function UsageBasedCost({ cost }: { cost: UsageBasedPriceWithOptionalAdvice }): JSX.Element {
  const showAdvice = cost.advice != null;
  const showAsIncluded = cost.price === 0 && !showAdvice;

  return (
    <>
      <div className="pricedetails__label">
        <div>
          {cost.title}
          {!showAsIncluded && showUnit(cost) && (
            <>
              {' '}
              (<UnitInclConsumption unit={cost.unit} />)
            </>
          )}
        </div>
      </div>
      <div className="pricedetails__price">
        <UsageBasedPriceDisplay
          price={cost.price}
          showAsIncluded={showAsIncluded}
          advice={cost.advice}
        />
      </div>
    </>
  );
}

export default function PriceDetails({ priceInfo }: PriceDetailsProps): JSX.Element {
  const hasAdditionalCosts = priceInfo.payableHost.length > 0 || priceInfo.optional.length > 0;
  const hasOptionalCosts = priceInfo.optional.length > 0;

  return (
    <div className="pricedetails">
      <h3>{I18n.t('travel_booking_price.costs_in_detail')}</h3>
      <h4>{I18n.t('travel_booking_price.payable_to_casapilot')}</h4>
      <div className="pricedetails__list">
        <div className="pricedetails__row">
          <div className="pricedetails__label">
            {I18n.t('travel_booking_price.rent_fees_included')}
          </div>
          <div className="pricedetails__price">{I18n.l('currency', priceInfo.priceRent)}</div>
        </div>
        {priceInfo.payableCasapilot.map((cost) => (
          <div key={cost.title} className="pricedetails__row">
            <FixedCost cost={cost} />
          </div>
        ))}
        <div className="pricedetails__row">
          <div className="pricedetails__label">
            <div>
              <div className="pricedetails__sum">{I18n.t('travel_booking_price.total_amount')}</div>
              {hasAdditionalCosts && (
                <div className="pricedetails__unit">
                  {I18n.t('travel_booking_price.plus_other_costs')}
                </div>
              )}
            </div>
          </div>
          <div className="pricedetails__price  pricedetails__sum">
            {I18n.l('currency', priceInfo.totalPrice)}
          </div>
        </div>
      </div>

      {priceInfo.payableHost.length > 0 && (
        <>
          <h4>{I18n.t('travel_booking_price.payable_on_location')}</h4>
          <div className="pricedetails__list">
            {priceInfo.payableHost.map((cost) => (
              <div key={cost.title} className="pricedetails__row">
                <FixedCost cost={cost} />
              </div>
            ))}
          </div>
        </>
      )}

      <h4>{I18n.t('activerecord.attributes.accommodation.bond')}</h4>
      <div className="pricedetails__list">
        <div className="pricedetails__row">
          <div className="pricedetails__label">
            {I18n.t(`travel_booking_price.deposit.${priceInfo.securityDepositPaymentType}`)},
            <br />
            {I18n.t(`travel_booking_price.deposit.refund.${priceInfo.securityDepositRefund}`)}
          </div>
          <div className="pricedetails__price">{I18n.l('currency', priceInfo.securityDeposit)}</div>
        </div>
        {priceInfo.withheldFromDeposit.length > 0 && (
          <>
            <div className="pricedetails__row">
              <div className="pricedetails__label">
                {I18n.t('travel_booking_price.withheld_from_deposit')}
              </div>
            </div>
            {priceInfo.withheldFromDeposit.map((cost) => (
              <div key={cost.title} className="pricedetails__row">
                <UsageBasedCost cost={cost} />
              </div>
            ))}
          </>
        )}
      </div>

      {hasOptionalCosts && (
        <>
          <h4>{I18n.t('travel_booking_price.other_costs')}</h4>
          <div className="pricedetails__list">
            {priceInfo.optional.map((cost) => (
              <div key={cost.title} className="pricedetails__row">
                <UsageBasedCost cost={cost} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
